import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Obtiene las temporadas que coinciden con una fecha específica
 * @param {Date} fecha - Fecha para la que se buscan temporadas
 * @returns {Promise<Array>} - Array de temporadas que incluyen la fecha
 */
export const obtenerTemporadasPorFecha = async (fecha) => {
  try {
    // Imprimir la fecha recibida para debugging
    console.log('Fecha recibida para consulta de temporadas:', fecha);
    
    // Asegurarse de que la fecha sea un objeto Date
    let fechaConsulta;
    if (fecha instanceof Date) {
      fechaConsulta = fecha;
    } else if (typeof fecha === 'string') {
      fechaConsulta = new Date(fecha);
      // Para strings de fecha, asegurar que se interpretan en zona horaria local
      // Este paso es crucial para evitar problemas de zona horaria
      if (fecha.includes('T') === false) {
        // Si es formato YYYY-MM-DD, añadir hora para evitar problemas con zonas horarias
        fechaConsulta = new Date(fecha + 'T12:00:00');
      }
    } else {
      fechaConsulta = new Date();
    }
    
    // Normalizar a inicio del día para comparaciones consistentes
    fechaConsulta.setHours(0, 0, 0, 0);
    
    console.log('Fecha normalizada para consulta:', fechaConsulta);
    
    // Consultar temporadas donde la fecha está entre fechaInicio y fechaFin
    const temporadasRef = collection(db, 'temporadas');
    const q = query(
      temporadasRef,
      where('fechaInicio', '<=', fechaConsulta),
      where('fechaFin', '>=', fechaConsulta),
      orderBy('fechaInicio', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    console.log('Temporadas encontradas:', querySnapshot.size);
    
    // Si hay temporadas, devolverlas como array de objetos
    if (!querySnapshot.empty) {
      const temporadas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Datos de temporadas:', temporadas);
      return temporadas;
    }
    
    return [];
  } catch (error) {
    console.error("Error al obtener temporadas:", error);
    return [];
  }
};

/**
 * Obtiene el precio de una habitación por ocupación, considerando temporadas
 * @param {Object} habitacion - Objeto de habitación
 * @param {Number} ocupacion - Número de personas
 * @param {Date} fecha - Fecha para la que se calcula el precio (opcional)
 * @returns {Promise<Number>} - Precio calculado
 */
export const obtenerPrecioPorOcupacionYFecha = async (habitacion, ocupacion, fecha = null) => {
  try {
    // Convertir ocupación a número
    const ocupacionNum = parseInt(ocupacion) || 1;
    
    // Si la ocupación es mayor a la capacidad, usar la capacidad máxima
    const ocupacionAjustada = Math.min(ocupacionNum, habitacion.capacity || habitacion.capacidad || 1);
    
    // Si no se proporciona fecha, devolver precios normales
    if (!fecha) {
      // Si no tiene precios por ocupación, usar el precio base
      if (!habitacion.preciosPorOcupacion || Object.keys(habitacion.preciosPorOcupacion).length === 0) {
        return habitacion.precio || 0;
      }
      
      // Obtener el precio según la ocupación
      if (habitacion.preciosPorOcupacion[ocupacionAjustada]) {
        return parseInt(habitacion.preciosPorOcupacion[ocupacionAjustada]) || 0;
      }
      
      return habitacion.precio || 0;
    }
    
    // Buscar temporadas para la fecha específica
    const temporadas = await obtenerTemporadasPorFecha(fecha);
    
    // Si no hay temporadas, devolver precio normal
    if (temporadas.length === 0) {
      // Si no tiene precios por ocupación, usar el precio base
      if (!habitacion.preciosPorOcupacion || Object.keys(habitacion.preciosPorOcupacion).length === 0) {
        return habitacion.precio || 0;
      }
      
      // Obtener el precio según la ocupación
      if (habitacion.preciosPorOcupacion[ocupacionAjustada]) {
        return parseInt(habitacion.preciosPorOcupacion[ocupacionAjustada]) || 0;
      }
      
      return habitacion.precio || 0;
    }
    
    // Usar la primera temporada (si hay varias, se ordenan por fechaInicio descendente)
    const temporada = temporadas[0];
    
    // Verificar si la temporada tiene precios para esta habitación y ocupación
    if (
      temporada.precios && 
      temporada.precios[habitacion.id] && 
      temporada.precios[habitacion.id][ocupacionAjustada]
    ) {
      return parseInt(temporada.precios[habitacion.id][ocupacionAjustada]) || 0;
    }
    
    // Si no hay precio específico en la temporada, usar el precio normal
    if (habitacion.preciosPorOcupacion && habitacion.preciosPorOcupacion[ocupacionAjustada]) {
      return parseInt(habitacion.preciosPorOcupacion[ocupacionAjustada]) || 0;
    }
    
    return habitacion.precio || 0;
  } catch (error) {
    console.error("Error al calcular precio:", error);
    
    // En caso de error, devolver el precio normal
    if (habitacion.preciosPorOcupacion && habitacion.preciosPorOcupacion[ocupacion]) {
      return habitacion.preciosPorOcupacion[ocupacion];
    }
    return habitacion.precio || 0;
  }
};

/**
 * Versión sincrónica para obtener precio por ocupación (sin temporadas)
 * @param {Object} habitacion - Objeto de habitación
 * @param {Number} ocupacion - Número de personas
 * @returns {Number} - Precio calculado
 */
export const obtenerPrecioPorOcupacion = (habitacion, ocupacion) => {
  // Convertir ocupación a número
  const ocupacionNum = parseInt(ocupacion) || 1;
  
  // Si la ocupación es mayor a la capacidad, usar la capacidad máxima
  const ocupacionAjustada = Math.min(ocupacionNum, habitacion.capacity || habitacion.capacidad || 1);
  
  // Si no tiene precios por ocupación, usar el precio base
  if (!habitacion.preciosPorOcupacion || Object.keys(habitacion.preciosPorOcupacion).length === 0) {
    return habitacion.precio || 0;
  }
  
  // Obtener el precio según la ocupación
  if (habitacion.preciosPorOcupacion[ocupacionAjustada]) {
    return parseInt(habitacion.preciosPorOcupacion[ocupacionAjustada]) || 0;
  }
  
  return habitacion.precio || 0;
}; 